// PDF 出力範囲（キャンバス矩形）の境界を、画面上 1px の点線と「PDF出力範囲」の注釈で示す overlay。
//
// - 境界の正本は useCanvasRect（サイズプリセットから求めたキャンバス矩形）で、全体像 PDF・印刷・サムネイルが
//   出力する範囲と同じ矩形を、現在の pan / zoom で画面座標へ投影して描く。
// - 図形・選択・コメントより背面（ステージより前の兄弟要素）に置き、pointer-events を持たないため、
//   クリック・ドラッグ・範囲選択などの操作を一切遮らない。範囲外への図形の配置もこれまでどおり許可する。
// - 画面操作のための装飾であり、全体像 PDF・印刷・サムネイル（いずれも図形データからの独立した
//   Canvas2D 描画）には描かれず、作図データとしても保存しない。
// - 点線は画面に見えている区間だけを描く。ズームで矩形が画面の何十倍にも広がっても、巨大な要素や
//   長大な破線のパスを作らない。破線の模様は角を起点にそろえ、パンしても模様が流れないようにする。

import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";
import type { CanvasFrame } from "@refinement-canvas/domain";
import type { ViewState } from "../../lib/canvas-coordinates";
import { useCanvasStore } from "./store/canvas-store";
import { useCanvasRect } from "./use-canvas-rect";
import {
  APP_MENU_HEIGHT_VARIABLE,
  BOTTOM_CONTROLS_HEIGHT_VARIABLE,
} from "./use-panel-size-reserve";

/** 注釈の固定文言。 */
export const PDF_BOUNDARY_LABEL_TEXT = "PDF出力範囲";

/** 画面座標（CSS px）の矩形。 */
export interface ScreenRect {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

export interface ViewportSize {
  readonly width: number;
  readonly height: number;
}

export interface LabelSize {
  readonly width: number;
  readonly height: number;
}

/**
 * 注釈を置かない画面の余白（CSS px）。上は左上のアプリメニュー・右上の参加者一覧、下は下部中央の
 * ツールバーの高さぶんを空け、注釈がこれらの操作 UI に隠れたり重なったりしないようにする。
 * 左上のアプリメニューと画面右下の下部コントロール（全体マップ・ズームパネル・FAB 用の空き領域）は、
 * 実測の高さがこの既定より大きければ、その帯まで広げる（resolveLabelSafeInsets）。
 */
export interface LabelSafeInsets {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

export const PDF_BOUNDARY_LABEL_SAFE_INSETS: LabelSafeInsets = {
  top: 72,
  right: 16,
  bottom: 96,
  left: 16,
};

/** アプリメニューと画面の上端の間隔（styles.css の .canvas-app-menu の top と同じ 16px）。 */
const APP_MENU_EDGE_OFFSET_PX = 16;
/**
 * 下部コントロールと画面の下端の間隔の既定値。styles.css の --canvas-bottom-controls-bottom
 * （16px + セーフエリアの下端の余白）からセーフエリアを除いた 16px で、実測できないときに使う。
 */
const BOTTOM_CONTROLS_EDGE_OFFSET_PX = 16;
/** アプリメニュー・下部コントロールと注釈の間隔。 */
const PANEL_LABEL_GAP_PX = 8;

/**
 * 実測した操作 UI の寸法（CSS px）。高さは usePanelSizeReserve がルート要素の CSS 変数へ書き込んだ値、
 * 下部コントロールの下端の間隔は見えない要素で実測した値で、測れていなければ undefined（未測定）。
 */
export interface MeasuredPanelSizes {
  /** 左上のアプリメニューの高さ（--canvas-app-menu-height）。 */
  readonly appMenuHeight?: number | undefined;
  /** 画面右下の下部コントロールの高さ（--canvas-bottom-controls-height）。 */
  readonly bottomControlsHeight?: number | undefined;
  /**
   * 下部コントロールと画面の下端の間隔（--canvas-bottom-controls-bottom を解決した値で、セーフエリアの
   * 下端の余白を含む）。未測定なら既定の 16px とみなす。
   */
  readonly bottomControlsOffset?: number | undefined;
}

/**
 * 既定の余白を、実測したアプリメニュー（上）と下部コントロール（下）の帯が大きければそれに合わせて広げる。
 * 全体マップは画面右下で既定の下余白より高く積まれるため、広げないと右下寄りの注釈（右下向きの矢印つきの
 * 注釈など）が全体マップの背面に隠れる。アプリメニューも狭い画面では折り返して高くなる。
 * 下部コントロールの帯は、セーフエリアの下端の余白を含む実際の間隔から求める（viewport-fit=cover のため、
 * ホームインジケーターのある端末では全体マップがその余白の分だけ上にある）。
 */
export function resolveLabelSafeInsets(
  measured: MeasuredPanelSizes,
  base: LabelSafeInsets = PDF_BOUNDARY_LABEL_SAFE_INSETS
): LabelSafeInsets {
  const bottomOffset = isPositiveFinite(measured.bottomControlsOffset)
    ? measured.bottomControlsOffset
    : BOTTOM_CONTROLS_EDGE_OFFSET_PX;
  return {
    ...base,
    top: Math.max(base.top, panelBandPx(APP_MENU_EDGE_OFFSET_PX, measured.appMenuHeight)),
    bottom: Math.max(base.bottom, panelBandPx(bottomOffset, measured.bottomControlsHeight)),
  };
}

/**
 * 画面の縁から操作 UI の帯を越えた位置までの距離（縁との間隔 + 高さ + 注釈との間隔）。
 * 高さが未測定・不正な値なら 0（既定の余白に任せる）。
 */
function panelBandPx(edgeOffset: number, height: number | undefined): number {
  if (!isPositiveFinite(height)) return 0;
  return edgeOffset + height + PANEL_LABEL_GAP_PX;
}

function isPositiveFinite(value: number | undefined): value is number {
  return value !== undefined && Number.isFinite(value) && value > 0;
}

/** 注釈の大きさの初期見積もり（実測できるまでの配置計算に使う）。 */
const LABEL_SIZE_ESTIMATE: LabelSize = { width: 84, height: 20 };
/**
 * 安全域がこれより狭い画面では、余白を使わず画面全体へ注釈を置く。判定に注釈の実寸を使わないのは、
 * 実寸（矢印の有無で変わる）で安全域が切り替わると置き方（辺 / 方向）と文言が交互に入れ替わり得るため。
 */
const MIN_SAFE_AREA: LabelSize = { width: 160, height: 48 };
/** 境界線と注釈の間隔。 */
const LABEL_GAP_PX = 4;
/** 破線の 1 周期（線 3px + 間隔 3px）。styles.css の stroke-dasharray と一致させる。 */
export const PDF_BOUNDARY_DASH_PERIOD_PX = 6;
/** 点線を画面の外へ少しだけ延ばす量。線の端が画面の縁に見えないようにする。 */
const LINE_CLIP_MARGIN_PX = 8;

export type PdfBoundaryEdge = "top" | "right" | "bottom" | "left";

export interface PdfBoundarySegment {
  readonly edge: PdfBoundaryEdge;
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
  /** 角を起点にした破線の位相（stroke-dashoffset）。 */
  readonly dashOffset: number;
}

export type PdfBoundaryDirection =
  | "up"
  | "down"
  | "left"
  | "right"
  | "up-left"
  | "up-right"
  | "down-left"
  | "down-right";

export type PdfBoundaryLabelPlacement =
  | {
      /** 見えている辺に沿って置く（outside は境界の外側に置けたか）。 */
      readonly mode: "edge";
      readonly edge: PdfBoundaryEdge;
      readonly outside: boolean;
      readonly x: number;
      readonly y: number;
    }
  | {
      /** 辺が見えないときに、境界のある方向の画面端へ矢印つきで置く。 */
      readonly mode: "direction";
      readonly direction: PdfBoundaryDirection;
      readonly x: number;
      readonly y: number;
    };

const DIRECTION_ARROWS: Record<PdfBoundaryDirection, string> = {
  up: "↑",
  down: "↓",
  left: "←",
  right: "→",
  "up-left": "↖",
  "up-right": "↗",
  "down-left": "↙",
  "down-right": "↘",
};

export function PdfBoundaryOverlay(): JSX.Element {
  const view = useCanvasStore((s) => s.view);
  const canvasRect = useCanvasRect();
  const viewport = useWindowSize();
  const panelSizes = useMeasuredPanelSizes();
  const bottomOffsetProbeRef = useRef<HTMLDivElement | null>(null);
  const bottomControlsOffset = useElementHeight(bottomOffsetProbeRef);
  const labelRef = useRef<HTMLDivElement | null>(null);
  const [labelSize, setLabelSize] = useState<LabelSize>(LABEL_SIZE_ESTIMATE);

  const screenRect = projectFrameToScreen(canvasRect, view);
  const segments = resolvePdfBoundarySegments(screenRect, viewport);
  const placement = resolvePdfBoundaryLabelPlacement({
    rect: screenRect,
    viewport,
    label: labelSize,
    insets: resolveLabelSafeInsets({ ...panelSizes, bottomControlsOffset }),
  });
  const labelText =
    placement?.mode === "direction"
      ? `${DIRECTION_ARROWS[placement.direction]} ${PDF_BOUNDARY_LABEL_TEXT}`
      : PDF_BOUNDARY_LABEL_TEXT;

  // 注釈の実寸（文言・矢印の有無・フォントの読み込みで変わる）を測り、画面内へ収める計算へ反映する。
  // パンのたびに寸法を読むとレイアウトを強制するため、大きさが変わったときだけ ResizeObserver で測り直す。
  // 測れない環境（jsdom 等）では初期見積もりのまま配置する。
  const labelVisible = placement !== null;
  useLayoutEffect(() => {
    const element = labelRef.current;
    if (!element) return undefined;
    const measure = (): void => {
      const width = element.offsetWidth;
      const height = element.offsetHeight;
      if (width <= 0 || height <= 0) return;
      setLabelSize((current) =>
        current.width === width && current.height === height ? current : { width, height }
      );
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [labelVisible]);

  return (
    <div aria-hidden className="canvas-pdf-boundary" data-testid="canvas-pdf-boundary">
      {/* 下部コントロールと画面の下端の間隔（セーフエリアの余白を含む）を実測するための見えない要素。 */}
      <div ref={bottomOffsetProbeRef} className="canvas-pdf-boundary-probe" />
      <svg
        className="canvas-pdf-boundary-lines"
        width={viewport.width}
        height={viewport.height}
        focusable="false"
      >
        {segments.map((segment) => (
          <line
            key={segment.edge}
            className="canvas-pdf-boundary-line"
            data-edge={segment.edge}
            x1={segment.x1}
            y1={segment.y1}
            x2={segment.x2}
            y2={segment.y2}
            strokeDashoffset={segment.dashOffset}
          />
        ))}
      </svg>
      {placement ? (
        <div
          ref={labelRef}
          className="canvas-pdf-boundary-label"
          data-testid="canvas-pdf-boundary-label"
          data-mode={placement.mode}
          data-placement={placement.mode === "edge" ? placement.edge : placement.direction}
          style={{ transform: `translate(${placement.x}px, ${placement.y}px)` }}
        >
          {labelText}
        </div>
      ) : null}
    </div>
  );
}

/** ステージ座標の矩形を、現在の pan / zoom で画面座標へ投影する。 */
export function projectFrameToScreen(frame: CanvasFrame, view: ViewState): ScreenRect {
  return {
    left: view.panX + frame.x * view.zoom,
    top: view.panY + frame.y * view.zoom,
    right: view.panX + (frame.x + frame.w) * view.zoom,
    bottom: view.panY + (frame.y + frame.h) * view.zoom,
  };
}

/** 画面全体が PDF 出力範囲の内側か（このとき範囲外は見えていない）。 */
export function isViewportInsideRect(rect: ScreenRect, viewport: ViewportSize): boolean {
  return (
    rect.left <= 0 &&
    rect.top <= 0 &&
    rect.right >= viewport.width &&
    rect.bottom >= viewport.height
  );
}

/**
 * 画面に見えている境界の区間を、辺ごとの線分として返す。1px の線をにじませないよう線の位置は
 * 画素の中心（整数 + 0.5）へそろえ、破線の位相は各辺の始点（左上側の角）を起点にする。
 */
export function resolvePdfBoundarySegments(
  rect: ScreenRect,
  viewport: ViewportSize
): readonly PdfBoundarySegment[] {
  if (!isFiniteRect(rect) || !(viewport.width > 0) || !(viewport.height > 0)) return [];
  const minX = -LINE_CLIP_MARGIN_PX;
  const maxX = viewport.width + LINE_CLIP_MARGIN_PX;
  const minY = -LINE_CLIP_MARGIN_PX;
  const maxY = viewport.height + LINE_CLIP_MARGIN_PX;
  const segments: PdfBoundarySegment[] = [];

  const horizontal: readonly [PdfBoundaryEdge, number][] = [
    ["top", rect.top],
    ["bottom", rect.bottom],
  ];
  for (const [edge, y] of horizontal) {
    if (y < minY || y > maxY) continue;
    const start = Math.max(rect.left, minX);
    const end = Math.min(rect.right, maxX);
    if (end <= start) continue;
    const lineY = snapToPixelCenter(y);
    segments.push({
      edge,
      x1: start,
      y1: lineY,
      x2: end,
      y2: lineY,
      dashOffset: positiveModulo(start - rect.left, PDF_BOUNDARY_DASH_PERIOD_PX),
    });
  }

  const vertical: readonly [PdfBoundaryEdge, number][] = [
    ["left", rect.left],
    ["right", rect.right],
  ];
  for (const [edge, x] of vertical) {
    if (x < minX || x > maxX) continue;
    const start = Math.max(rect.top, minY);
    const end = Math.min(rect.bottom, maxY);
    if (end <= start) continue;
    const lineX = snapToPixelCenter(x);
    segments.push({
      edge,
      x1: lineX,
      y1: start,
      x2: lineX,
      y2: end,
      dashOffset: positiveModulo(start - rect.top, PDF_BOUNDARY_DASH_PERIOD_PX),
    });
  }
  return segments;
}

/**
 * 「PDF出力範囲」の注釈の置き場所を決める。範囲外が画面に見えていなければ null（表示しない）。
 *
 * 1. 上辺が画面（操作 UI を避けた安全域）に見えていれば、その可視区間の左寄りに、辺の外側（上）へ置く。
 * 2. 上辺が見えなければ、左 → 右 → 下の順に、最初に見えている辺の外側へ置く。
 *    四隅が画面外でも、辺の一部が見えていれば表示する。外側に注釈の入る余白が無いときだけ内側へ置く。
 * 3. どの辺も見えない（画面全体が範囲外で境界線も見えない、または辺が操作 UI の下にしか無い）ときは、
 *    最寄りの境界がある方向の画面端へ、方向を示す矢印つきで置く。
 *
 * 注釈全体が安全域（画面から余白 insets を除いた領域。既定は PDF_BOUNDARY_LABEL_SAFE_INSETS で、
 * 画面の描画では resolveLabelSafeInsets で実測の操作 UI の帯まで広げた余白を渡す）へ収まるように丸める。
 */
export function resolvePdfBoundaryLabelPlacement(params: {
  readonly rect: ScreenRect;
  readonly viewport: ViewportSize;
  readonly label: LabelSize;
  readonly insets?: LabelSafeInsets | undefined;
}): PdfBoundaryLabelPlacement | null {
  const { rect, viewport, label } = params;
  if (!isFiniteRect(rect) || !(viewport.width > 0) || !(viewport.height > 0)) return null;
  if (isViewportInsideRect(rect, viewport)) return null;
  const safe = resolveSafeArea(viewport, params.insets ?? PDF_BOUNDARY_LABEL_SAFE_INSETS);

  for (const edge of ["top", "left", "right", "bottom"] as const) {
    const placement = placeAlongEdge(edge, rect, safe, label);
    if (placement) return placement;
  }
  return placeTowardBoundary(rect, viewport, safe, label);
}

function placeAlongEdge(
  edge: PdfBoundaryEdge,
  rect: ScreenRect,
  safe: ScreenRect,
  label: LabelSize
): PdfBoundaryLabelPlacement | null {
  if (edge === "top" || edge === "bottom") {
    const y = edge === "top" ? rect.top : rect.bottom;
    if (y < safe.top || y > safe.bottom) return null;
    const visibleStart = Math.max(rect.left, safe.left);
    const visibleEnd = Math.min(rect.right, safe.right);
    if (visibleEnd <= visibleStart) return null;
    const outsideY = edge === "top" ? y - LABEL_GAP_PX - label.height : y + LABEL_GAP_PX;
    const outside = outsideY >= safe.top && outsideY + label.height <= safe.bottom;
    const insideY = edge === "top" ? y + LABEL_GAP_PX : y - LABEL_GAP_PX - label.height;
    return {
      mode: "edge",
      edge,
      outside,
      x: clamp(visibleStart, safe.left, safe.right - label.width),
      y: clamp(outside ? outsideY : insideY, safe.top, safe.bottom - label.height),
    };
  }
  const x = edge === "left" ? rect.left : rect.right;
  if (x < safe.left || x > safe.right) return null;
  const visibleStart = Math.max(rect.top, safe.top);
  const visibleEnd = Math.min(rect.bottom, safe.bottom);
  if (visibleEnd <= visibleStart) return null;
  const outsideX = edge === "left" ? x - LABEL_GAP_PX - label.width : x + LABEL_GAP_PX;
  const outside = outsideX >= safe.left && outsideX + label.width <= safe.right;
  const insideX = edge === "left" ? x + LABEL_GAP_PX : x - LABEL_GAP_PX - label.width;
  return {
    mode: "edge",
    edge,
    outside,
    x: clamp(outside ? outsideX : insideX, safe.left, safe.right - label.width),
    y: clamp(visibleStart, safe.top, safe.bottom - label.height),
  };
}

function placeTowardBoundary(
  rect: ScreenRect,
  viewport: ViewportSize,
  safe: ScreenRect,
  label: LabelSize
): PdfBoundaryLabelPlacement {
  const center = { x: viewport.width / 2, y: viewport.height / 2 };
  let dx = 0;
  let dy = 0;
  const centerInside =
    center.x > rect.left && center.x < rect.right && center.y > rect.top && center.y < rect.bottom;
  if (centerInside) {
    // 画面の中央が範囲内なら、いちばん近い辺の方向を指す。
    const distances: readonly [number, number, number][] = [
      [center.y - rect.top, 0, -1],
      [rect.bottom - center.y, 0, 1],
      [center.x - rect.left, -1, 0],
      [rect.right - center.x, 1, 0],
    ];
    // 4 辺までの距離のうち最小のもの（同じ距離なら先に並べた上 → 下 → 左 → 右を優先）を選ぶ。
    const nearest = distances.reduce((best, entry) => (entry[0] < best[0] ? entry : best));
    dx = nearest[1];
    dy = nearest[2];
  } else {
    // 画面の中央が範囲外なら、範囲内でいちばん近い点の方向を指す。
    const nearestX = clamp(center.x, rect.left, rect.right);
    const nearestY = clamp(center.y, rect.top, rect.bottom);
    dx = Math.sign(roundTiny(nearestX - center.x));
    dy = Math.sign(roundTiny(nearestY - center.y));
  }
  const x =
    dx < 0
      ? safe.left
      : dx > 0
        ? safe.right - label.width
        : clamp(center.x - label.width / 2, safe.left, safe.right - label.width);
  const y =
    dy < 0
      ? safe.top
      : dy > 0
        ? safe.bottom - label.height
        : clamp(center.y - label.height / 2, safe.top, safe.bottom - label.height);
  return { mode: "direction", direction: directionOf(dx, dy), x, y };
}

function directionOf(dx: number, dy: number): PdfBoundaryDirection {
  if (dy < 0) return dx < 0 ? "up-left" : dx > 0 ? "up-right" : "up";
  if (dy > 0) return dx < 0 ? "down-left" : dx > 0 ? "down-right" : "down";
  return dx < 0 ? "left" : "right";
}

/** 安全域。注釈が入らないほど画面が小さいときは、画面全体を使う。 */
function resolveSafeArea(viewport: ViewportSize, insets: LabelSafeInsets): ScreenRect {
  const safe = {
    left: insets.left,
    top: insets.top,
    right: viewport.width - insets.right,
    bottom: viewport.height - insets.bottom,
  };
  if (
    safe.right - safe.left < MIN_SAFE_AREA.width ||
    safe.bottom - safe.top < MIN_SAFE_AREA.height
  ) {
    return { left: 0, top: 0, right: viewport.width, bottom: viewport.height };
  }
  return safe;
}

function useWindowSize(): ViewportSize {
  const [size, setSize] = useState<ViewportSize>(() => ({
    width: typeof window === "undefined" ? 0 : window.innerWidth,
    height: typeof window === "undefined" ? 0 : window.innerHeight,
  }));
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = (): void => {
      setSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return size;
}

/**
 * usePanelSizeReserve がルート要素の style へ書き込む操作 UI の実測の高さを読み、値が変わったら
 * （パネルの開閉・折り返し・全体マップの表示切り替え）再描画する。パンのたびには読み直さない。
 */
function useMeasuredPanelSizes(): MeasuredPanelSizes {
  const [sizes, setSizes] = useState<MeasuredPanelSizes>(readMeasuredPanelSizes);
  useEffect(() => {
    if (typeof document === "undefined" || typeof MutationObserver === "undefined") {
      return undefined;
    }
    const update = (): void => {
      const next = readMeasuredPanelSizes();
      setSizes((current) =>
        current.appMenuHeight === next.appMenuHeight &&
        current.bottomControlsHeight === next.bottomControlsHeight
          ? current
          : next
      );
    };
    // 初回の描画の後（同じ描画で各パネルが書き込んだ値を含む）にも読み直す。
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["style"] });
    return () => observer.disconnect();
  }, []);
  return sizes;
}

/**
 * 要素の高さ（CSS px）を実測し、変わったら再描画する。下部コントロールの下端の間隔を測る見えない要素に使い、
 * 端末の向きの変更などでセーフエリアの余白が変われば ResizeObserver で測り直す。
 * 測れない環境（jsdom 等で高さが 0）では undefined。
 */
function useElementHeight(ref: RefObject<HTMLElement | null>): number | undefined {
  const [height, setHeight] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const measure = (): void => {
      const next = element.offsetHeight;
      setHeight(next > 0 ? next : undefined);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return height;
}

function readMeasuredPanelSizes(): MeasuredPanelSizes {
  if (typeof document === "undefined") return {};
  const style = document.documentElement.style;
  return {
    appMenuHeight: parsePixels(style.getPropertyValue(APP_MENU_HEIGHT_VARIABLE)),
    bottomControlsHeight: parsePixels(style.getPropertyValue(BOTTOM_CONTROLS_HEIGHT_VARIABLE)),
  };
}

/** usePanelSizeReserve が書き込む `<数値>px` を数値へ戻す。それ以外（未設定を含む）は undefined。 */
function parsePixels(value: string): number | undefined {
  const match = /^(\d+(?:\.\d+)?)px$/.exec(value.trim());
  return match ? Number(match[1]) : undefined;
}

function clamp(value: number, min: number, max: number): number {
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
}

function positiveModulo(value: number, period: number): number {
  return ((value % period) + period) % period;
}

function snapToPixelCenter(value: number): number {
  return Math.floor(value) + 0.5;
}

/** 浮動小数の誤差（1e-9 未満）を 0 とみなす。 */
function roundTiny(value: number): number {
  return Math.abs(value) < 1e-9 ? 0 : value;
}

function isFiniteRect(rect: ScreenRect): boolean {
  return (
    Number.isFinite(rect.left) &&
    Number.isFinite(rect.top) &&
    Number.isFinite(rect.right) &&
    Number.isFinite(rect.bottom)
  );
}
